import useSWR from 'swr';
import { portalFetcher } from './portalFetcher';

interface ClientInvoicesResponse {
  records: unknown[];
  paidCount: number;
  partialCount: number;
  unpaidCount: number;
  overdueCount: number;
}

interface ServiceRecord {
  serviceEndDate?: string | null;
  [key: string]: unknown;
}

export function usePortalDashboard() {
  const { data: invoicesData, isLoading: invoicesLoading } = useSWR<ClientInvoicesResponse>(
    '/client-view-invoices',
    portalFetcher,
    { revalidateOnFocus: true }
  );
  const { data: servicesData, isLoading: servicesLoading } = useSWR<{ records?: ServiceRecord[] }>(
    '/client-view-services',
    portalFetcher,
    { revalidateOnFocus: true }
  );
  const { data: ticketsData, isLoading: ticketsLoading } = useSWR<unknown[]>(
    '/client-view-support-ticket',
    portalFetcher,
    { revalidateOnFocus: true }
  );

  const invoices = invoicesData?.records ?? [];
  const services = servicesData?.records ?? [];
  const tickets = Array.isArray(ticketsData) ? ticketsData : [];

  const now = new Date();
  const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const expiringServicesCount = services.filter((s: ServiceRecord) => {
    const end = s.serviceEndDate ? new Date(s.serviceEndDate) : null;
    return end && end >= now && end <= in30Days;
  }).length;

  const totalDue = Array.isArray(invoicesData?.records)
    ? (invoicesData!.records as { invoiceAmount?: number; paymentTotal?: number }[]).reduce(
        (sum, inv) => {
          const amt = inv.invoiceAmount ?? 0;
          const paid = inv.paymentTotal ?? 0;
          if (paid >= amt) return sum;
          return sum + (amt - paid);
        },
        0
      )
    : 0;

  return {
    openInvoicesCount: invoicesData?.unpaidCount ?? 0,
    overdueInvoicesCount: invoicesData?.overdueCount ?? 0,
    totalDue,
    activeServicesCount: services.length,
    expiringServicesCount,
    openTicketsCount: tickets.length,
    isLoading: invoicesLoading || servicesLoading || ticketsLoading,
  };
}
